import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, SearchField } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import type { FixedAssetStatusFilter } from '@/api/handlers/resources/fixedAssets/requests';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    AssetDetailDrawer,
    AssetFormDrawer,
    AssetTable,
    DepreciationRunDialog,
    DisposeDialog,
    RegisterSummary,
    TieOutBar,
} from './components';
import { ASSET_STATUS_FILTERS } from './fixedAssetsMeta';
import type { UseFixedAssetsPageResult } from './useFixedAssetsPage';

type FixedAssetsPageViewProps = {
    page: UseFixedAssetsPageResult;
    /** `null` while the register is in flight — labels stay, values skeleton. */
    figures: UseFixedAssetsPageResult['figures'];
    isLoading: boolean;
};

/**
 * Render-only Fixed Assets register. The shell supplies the page title, so there
 * is none here and no page-root padding.
 *
 * Order follows the general-to-specific hierarchy: the four summary figures, then
 * the register-to-ledger tie-out (a first-class element, not a footnote), then
 * the controls, then the register itself. The overlays are rendered last and are
 * mutually exclusive.
 */
export const FixedAssetsPageView: React.FC<FixedAssetsPageViewProps> = ({
    page,
    figures,
    isLoading,
}) => {
    const { t } = useTranslation('pages');
    const { overlay } = page;

    return (
        <section
            aria-label={t('fixedAssets.title')}
            className="flex flex-col gap-4"
            {...getNodeTestIdProp('fixed-assets-section')}
        >
            <RegisterSummary figures={figures} />

            <TieOutBar
                error={page.tieOutError?.message ?? null}
                onRetry={page.onRetryTieOut}
                tieOut={page.tieOut}
            />

            <div
                className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
                {...getNodeTestIdProp('fixed-assets-toolbar')}
            >
                <div className="flex flex-wrap items-center gap-3">
                    <SearchField
                        aria-label={t('fixedAssets.searchAria')}
                        className="w-full sm:max-w-xs"
                        isDisabled={isLoading}
                        onChange={page.onSearchChange}
                        value={page.search}
                        {...getNodeTestIdProp('fixed-assets-search')}
                    >
                        <SearchField.Group>
                            <SearchField.SearchIcon />
                            <SearchField.Input
                                placeholder={t('fixedAssets.searchPlaceholder')}
                            />
                            <SearchField.ClearButton />
                        </SearchField.Group>
                    </SearchField>

                    <Segment
                        aria-label={t('fixedAssets.filters.aria')}
                        isDisabled={isLoading}
                        onSelectionChange={(key) =>
                            page.onStatusChange(key as FixedAssetStatusFilter)
                        }
                        selectedKey={page.status}
                        size="sm"
                        {...getNodeTestIdProp('fixed-assets-status-filter')}
                    >
                        {ASSET_STATUS_FILTERS.map((filter) => (
                            <Segment.Item
                                id={filter.id}
                                key={filter.id}
                                {...getNodeTestIdProp(
                                    `fixed-assets-filter-${filter.id}`,
                                )}
                            >
                                <Segment.Separator />
                                {t(filter.labelKey)}
                            </Segment.Item>
                        ))}
                    </Segment>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        isDisabled={isLoading}
                        onPress={page.onOpenRun}
                        variant="outline"
                        {...getNodeTestIdProp('fixed-assets-run-depreciation')}
                    >
                        <Icon aria-hidden icon="gravity-ui:calendar" />
                        {t('fixedAssets.actions.runDepreciation')}
                    </Button>
                    <Button
                        isDisabled={isLoading}
                        onPress={page.onOpenCreate}
                        variant="primary"
                        {...getNodeTestIdProp('fixed-assets-add')}
                    >
                        <Icon aria-hidden icon="gravity-ui:plus" />
                        {t('fixedAssets.actions.newAsset')}
                    </Button>
                </div>
            </div>

            <AssetTable
                ariaLabel={t('fixedAssets.tableAria')}
                emptyState={page.emptyState}
                isLoading={isLoading}
                onDispose={page.onOpenDispose}
                onEdit={page.onOpenEdit}
                onSelect={page.onOpenDetail}
                rows={page.rows}
            />

            <p className="text-xs text-muted">{t('fixedAssets.footerNote')}</p>

            <AssetDetailDrawer
                assetId={overlay.kind === 'DETAIL' ? overlay.assetId : null}
                categories={page.categories}
                locale={page.locale}
                onClose={page.onCloseOverlay}
                onDispose={page.onOpenDispose}
                onEdit={page.onOpenEdit}
            />

            {overlay.kind === 'FORM' ? (
                <AssetFormDrawer
                    accounts={page.accounts}
                    asset={overlay.asset}
                    categories={page.categories}
                    isOpen
                    // A fresh mount per open, so a second "New asset" never
                    // inherits the previous draft (the account drawer's rule).
                    key={`${overlay.asset?.id ?? 'new'}-${overlay.instanceId}`}
                    locale={page.locale}
                    onClose={page.onCloseOverlay}
                />
            ) : null}

            <DisposeDialog
                accounts={page.accounts}
                asset={overlay.kind === 'DISPOSE' ? overlay.asset : null}
                locale={page.locale}
                onClose={page.onCloseOverlay}
            />

            {overlay.kind === 'RUN' ? (
                <DepreciationRunDialog
                    isOpen
                    locale={page.locale}
                    onClose={page.onCloseOverlay}
                />
            ) : null}
        </section>
    );
};
